import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { equipmentService } from "../services/equipment.service";
import type {
	CreateEquipmentRequest,
	EquipmentListParams,
	ErrorResponse,
	LiveGpsParams,
	UpdateEquipmentRequest,
} from "../types";

export const equipmentKeys = {
	all: ["equipment"] as const,
	lists: () => [...equipmentKeys.all, "list"] as const,
	list: (params?: EquipmentListParams) => [...equipmentKeys.lists(), params] as const,
	details: () => [...equipmentKeys.all, "detail"] as const,
	detail: (id: string) => [...equipmentKeys.details(), id] as const,
	gps: (id: string) => [...equipmentKeys.detail(id), "gps"] as const,
};

export function useEquipmentList(params?: EquipmentListParams) {
	return useQuery({
		queryKey: equipmentKeys.list(params),
		queryFn: () => equipmentService.list(params),
		// If organizationId is explicitly provided it must be truthy; if omitted, always fetch
		enabled: params?.organizationId === undefined ? true : !!params.organizationId,
	});
}

export function useEquipmentLive(params?: LiveGpsParams, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [...equipmentKeys.all, "live", params] as const,
		queryFn: () => equipmentService.getLive(params),
		refetchInterval: 30_000,
		enabled: options?.enabled ?? true,
	});
}

export function useEquipment(id: string) {
	return useQuery({
		queryKey: equipmentKeys.detail(id),
		queryFn: () => equipmentService.getById(id),
		enabled: !!id,
	});
}

export function useEquipmentGps(id: string) {
	return useQuery({
		queryKey: equipmentKeys.gps(id),
		queryFn: () => equipmentService.getGps(id),
		enabled: !!id,
	});
}

export function useCreateEquipment() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateEquipmentRequest) => equipmentService.create(data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
			if (variables.organizationId) {
				queryClient.invalidateQueries({
					queryKey: equipmentKeys.list({ organizationId: variables.organizationId }),
				});
			}
			message.success("Texnika muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika yaratishda xatolik";
			message.error(msg);
		},
	});
}

export function useUpdateEquipment() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateEquipmentRequest }) =>
			equipmentService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: equipmentKeys.detail(variables.id),
			});
			message.success("Texnika muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika yangilashda xatolik";
			message.error(msg);
		},
	});
}

export function useDeleteEquipment() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => equipmentService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
			message.success("Texnika muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika o'chirishda xatolik";
			message.error(msg);
		},
	});
}
